import { asCur } from "@/helpers/inputHelpers";
import { getShortMortgageType } from "@/helpers/leningdeelHelpers";
import { AggregationState } from "@/enums/AggregationState";
import type { MortgagePart } from "@/models/Leningdeel";
import { defineStore } from "pinia";
import type { InterestAggregationResult } from "@/models/domain/InterestAggregationResult";
import type { InterestAggregationOpts } from "@/models/InterestAggregationOpts";
import { api } from "@/loanLinkApi";
import type { InterestAggregationExpanded } from "@/models/InterestAggregationExpanded";
import { useDossierDataStore } from "./useDossierDataStore";

export const useInterestStore = defineStore("interest", {
	state: () => ({
		aggregationResult: [] as InterestAggregationExpanded[],
		aggregationState: AggregationState.UNRETRIEVED,
		appliedRow: undefined as InterestAggregationExpanded | undefined,
		previousAggregationOpts: null as InterestAggregationOpts | null,
	}),
	actions: {
		async aggregate(interestAggregationOpts: InterestAggregationOpts) {
			try {
				this.aggregationState = AggregationState.RETRIEVING;
				this.previousAggregationOpts = interestAggregationOpts;

				const {
					useNHG,
					ltv,
					bouwaard,
					quotationValidFor,
					rentevastePeriodes,
					aflossingsvormen,
				} = interestAggregationOpts;

				const optimalInterestOptions = {
					useNHG,
					ltv: ltv ?? 0,
					bouwaard,
					quotationValidFor: quotationValidFor,
					rentevastePeriodes,
					aflossingsvormen: aflossingsvormen,
				};

				const { data } = await api.interest.getOptimalInterests(
					optimalInterestOptions,
				);

				const interestAggregation = data.result;

				if (!Array.isArray(interestAggregation)) {
					throw Error("retrieval error");
				}

				const cleanedInterestAggregation = [];
				const dossierDataStore = useDossierDataStore();

				for (const aggregationResult of interestAggregation) {
					const interestProductRow: InterestAggregationExpanded = Object.assign(
						aggregationResult,
						{
							weight: 0,
							weightedAverage: 0,
						},
					);
					let interestProductMatches = 0;

					for (const loanpart of dossierDataStore.data.mortgage.mortgageParts) {
						const matchedInterestProduct = this.findLoanpartMatch(
							loanpart,
							aggregationResult as InterestAggregationResult,
						);

						if (matchedInterestProduct === undefined) {
							continue;
						}

						interestProductRow[`interest_${loanpart.id}`] =
							matchedInterestProduct.interestRate;
						interestProductRow[loanpart.id] = matchedInterestProduct;

						interestProductRow["weight"] += asCur(loanpart.amount) ?? 0;
						interestProductRow["weightedAverage"] +=
							(asCur(loanpart.amount) ?? 0) *
							matchedInterestProduct.interestRate;

						interestProductMatches++;
					}

					if (
						interestProductMatches ===
						dossierDataStore.data.mortgage.mortgageParts.length
					) {
						interestProductRow["weightedAverage"] /=
							interestProductRow["weight"];

						cleanedInterestAggregation.push(interestProductRow);
					}
				}

				cleanedInterestAggregation.sort(
					(a, b) => a.weightedAverage - b.weightedAverage,
				);

				this.aggregationResult = cleanedInterestAggregation;
				this.aggregationState = AggregationState.RETRIEVED;
			} catch {
				this.aggregationResult = [];
				this.aggregationState = AggregationState.FAILED;
			}
		},
		hideAggregation() {
			this.aggregationState = AggregationState.UNRETRIEVED;
		},
		showAggregation() {
			this.aggregationState = AggregationState.RETRIEVED;
		},
		findLoanpartMatch(
			loanpart: MortgagePart,
			result: InterestAggregationResult,
		) {
			const matchedResult = result.products
				.filter(
					(r) =>
						r.fixedInterestPeriod === loanpart.fixedInterestMonths &&
						r.repaymentForm ===
							getShortMortgageType(loanpart.repaymentForm ?? ""),
				)
				.reduce(
					(
						result:
							| {
									fixedInterestPeriod: number;
									repaymentForm:
										| "annuïteit"
										| "lineair"
										| "aflossingsvrij"
										| "spaar"
										| "leven"
										| "overig";
									interestRate: number;
							  }
							| undefined,
						current,
					) => {
						return result === undefined ||
							current.interestRate < result.interestRate
							? current
							: result;
					},
					undefined,
				);

			return matchedResult;
		},
	},
});
